import type { ReactNode } from 'react';
import { SearchField } from '../product-table/filters/search-field-filter';

type SearchWithCountProps = {
  quantity: number;
  /**
   * Unfiltered baseline. Not displayed — used only as an invisible sizer so the
   * count cell reserves enough width for the widest possible `quantity`, and
   * the search box doesn't shift as the live count narrows.
   */
  total?: number;
  /** What's being counted, plural — e.g. "products". Shown as a quiet header label. */
  noun: string;
  /**
   * The search input to weld the count onto. Defaults to `<SearchField grouped />`,
   * which reads from `ProductTableContext` — appropriate for any surface using
   * `useFilteredProducts`. Non-products surfaces pass their own controlled
   * `<SearchInput grouped value={...} onChange={...} />`.
   */
  children?: ReactNode;
};

/**
 * Welds the result count onto the search bar as one bordered control: the
 * count is plainly the result of the search, it never disappears while typing,
 * and it doesn't fight the ⌘F hint (separate segment). The shared border lives
 * here; `SearchInput grouped` drops its own so this owns it. A real `border`
 * (not `ring-inset`) is used because the edge-to-edge opaque children paint
 * over an inset ring.
 *
 * Shows the current `quantity` only — when filtered to zero, the no-results
 * empty state is where context like "0 of N" belongs. `total` is consumed
 * only as an invisible sizer holding the widest possible value, so the cell
 * width stays steady across keystrokes (tabular-nums keeps digit columns
 * aligned).
 */
export function SearchWithCount({
  quantity,
  total,
  noun,
  children = <SearchField grouped />,
}: SearchWithCountProps) {
  const widest = total ?? quantity;

  return (
    <div className="flex min-w-0 flex-1 max-w-2xl items-stretch overflow-hidden rounded-sm border border-toolbar-edge bg-white focus-within:border-toolbar-edge-focus">
      {children}
      <div className="flex shrink-0 flex-col justify-center border-l border-toolbar-edge px-3 py-1 text-right">
        <span className="text-[10px] font-medium uppercase leading-tight tracking-wide text-gray-400">
          {noun}
        </span>
        <span className="grid justify-items-end text-sm leading-tight text-gray-600 tabular-nums">
          <span aria-hidden className="invisible col-start-1 row-start-1 whitespace-nowrap">
            {widest.toLocaleString()}
          </span>
          <span className="col-start-1 row-start-1 whitespace-nowrap">
            {quantity.toLocaleString()}
          </span>
        </span>
      </div>
    </div>
  );
}
